// A12 — SEO va ommaviy veb. Module-local data layer (does NOT touch
// `lib/adapters.ts` / `i18n/*` — C-session HOT). Backend `/admin/seo/*` is
// greenfield → fetchers fail-soft to empty; mutations POST when it ships.
// Registration = ./REGISTER.md (C applies routes/screenStatus/nav/i18n/perm).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

export interface RedirectRow {
  id: number
  from_path: string
  to_path: string
  code: 301 | 302
  hits: number
  created_at: string
}

export interface NotFoundRow {
  path: string
  hits: number
  last_seen: string
  suggestion: string | null
}

export function fetchRedirects(params: FetchParams): Promise<FetchResult<RedirectRow>> {
  return safeList<RedirectRow>('/admin/seo/redirects', 'redirects', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.from_path} ${r.to_path}`),
  )
}

export function createRedirect(body: {
  from_path: string
  to_path: string
  code: 301 | 302
}): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/admin/seo/redirects', body)
}

export function deleteRedirect(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/seo/redirects/${id}`)
}

export function fetch404s(params: FetchParams): Promise<FetchResult<NotFoundRow>> {
  return safeList<NotFoundRow>('/admin/seo/not-found', 'entries', params.signal).then((rows) =>
    toResult(rows, params, (r) => r.path),
  )
}

/* ── Generic SEO config CRUD (greenfield `/admin/seo/*`, fail-soft) ────────────
 * Meta-shablon / canonical / hreflang / noindex / schema / sitemap / keyword /
 * landing / blog / mini-app / deep-link ekranlari bir xil "ro'yxat + qo'shish +
 * o'chirish" naqshi. Bitta generic qatlam ularning hammasini xizmat qiladi.
 * Backend ulanmaguncha ro'yxat bo'sh (safeList), mutatsiya POST/DELETE qiladi. */

/** Minimal shakl — har config-qatorda `id` bo'ladi (backend qaytaradi). */
export interface SeoConfigRow {
  id: number | string
  [key: string]: unknown
}

/** Ro'yxat fetcher fabrikasi. `searchKeys` — tez qidiruvda solishtiriuvchi maydonlar. */
export function seoList<T extends SeoConfigRow>(
  path: string,
  key: string,
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeList<T>(path, key, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

export function seoCreate(
  path: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>(path, body)
}

export function seoDelete(path: string, id: number | string): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`${path}/${encodeURIComponent(String(id))}`)
}

/** A12.01 — SEO dashboard: LIVE endpoint'lardan (redirects/404/robots) yig'ma. */
export interface SeoSummary {
  redirects: number
  notFound: number
  notFoundHits: number
  robotsBytes: number
  robotsPresent: boolean
}

export async function fetchSeoSummary(signal: AbortSignal): Promise<SeoSummary> {
  const [reds, nf, robots] = await Promise.all([
    safeList<RedirectRow>('/admin/seo/redirects', 'redirects', signal),
    safeList<NotFoundRow>('/admin/seo/not-found', 'entries', signal),
    getRobots(signal),
  ])
  return {
    redirects: reds.length,
    notFound: nf.length,
    notFoundHits: nf.reduce((sum, r) => sum + (Number(r.hits) || 0), 0),
    robotsBytes: robots.length,
    robotsPresent: robots.trim().length > 0,
  }
}

/** A12.05 — robots.txt (dormant → bo'sh matn). */
export async function getRobots(signal?: AbortSignal): Promise<string> {
  try {
    const d = await api.get<{ content: string }>(
      '/admin/seo/robots',
      signal ? { signal } : undefined,
    )
    return d?.content ?? ''
  } catch {
    return ''
  }
}

export function saveRobots(content: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/admin/seo/robots', { content })
}

/* ── A12.17–A12.21 config screens (seo_* app_settings) ─────────────────────────
 * Core Web Vitals / PageSpeed / mobile / Search Console / Yandex Webmaster store
 * manually-entered metrics + provider API keys. `settings` holds non-secret
 * values; `secrets` flags whether each secret key is configured (its value is
 * never returned). Saving a blank secret leaves the stored key untouched. */
export interface SeoSettings {
  settings: Record<string, string>
  secrets: Record<string, boolean>
}

export async function seoGetSettings(signal?: AbortSignal): Promise<SeoSettings> {
  try {
    const d = await api.get<SeoSettings>(
      '/admin/seo/settings',
      signal ? { signal } : undefined,
    )
    return { settings: d?.settings ?? {}, secrets: d?.secrets ?? {} }
  } catch {
    return { settings: {}, secrets: {} }
  }
}

export function seoSaveSettings(
  values: Record<string, string>,
): Promise<{ ok: boolean; saved?: number }> {
  return api.put<{ ok: boolean; saved?: number }>('/admin/seo/settings', values)
}

/* ── A12.26 SEO audit (computed from the real SEO tables, no crawler) ──────────*/
export interface SeoAuditCheck {
  id: string
  label: string
  severity: 'ok' | 'warn' | 'error'
  count: number
  detail: string
}
export interface SeoAuditReport {
  score: number
  issues: number
  checks: SeoAuditCheck[]
  generated_at: string
}

export async function fetchSeoAudit(signal?: AbortSignal): Promise<SeoAuditReport | null> {
  try {
    return await api.get<SeoAuditReport>('/admin/seo/audit', signal ? { signal } : undefined)
  } catch {
    return null
  }
}

/** Module-local RU strings (i18n HOT → keydan keyin ko'chiriladi, REGISTER.md). */
export const SEO_STR = {
  redirects: 'Редиректы',
  notFound: 'Мониторинг 404',
  robots: 'robots.txt',
  from: 'Откуда',
  to: 'Куда',
  code: 'Код',
  hits: 'Переходы',
  created: 'Создан',
  path: 'Путь',
  lastSeen: 'Последний раз',
  suggestion: 'Предложение',
  add: 'Добавить редирект',
  delete: 'Удалить',
  save: 'Сохранить',
  saved: 'robots.txt сохранён',
  saveFailed: 'Не удалось сохранить',
  created2: 'Редирект создан',
  deleted: 'Редирект удалён',
  failed: 'Не удалось выполнить',
  // ── Generic config-CRUD ─────────────────────────────────────────
  createdGeneric: 'Запись создана',
  deletedGeneric: 'Запись удалена',
  // ── A12.01 SEO dashboard (real) ─────────────────────────────────
  dashboard: 'SEO панель',
  kpi404Hits: 'Переходы на 404',
  robotsState: 'robots.txt',
  present: 'Настроен',
  absent: 'Не настроен',
  refresh: 'Обновить',
  open: 'Открыть',
  loadFailed: 'Не удалось загрузить',
  // ── A12.06 sitemap ──────────────────────────────────────────────
  sitemap: 'Sitemap.xml',
  loc: 'URL',
  changefreq: 'Частота',
  priority: 'Приоритет',
  addSitemap: 'Добавить URL',
  // ── A12.02 meta templates ───────────────────────────────────────
  metaTemplates: 'Шаблоны мета-тегов',
  pattern: 'Шаблон пути',
  metaTitleCol: 'Title',
  metaDesc: 'Description',
  addMeta: 'Добавить шаблон',
  // ── A12.03 OpenGraph / Twitter ──────────────────────────────────
  openGraph: 'OpenGraph / Twitter Card',
  image: 'Изображение',
  addOg: 'Добавить карточку',
  // ── A12.04 canonical ────────────────────────────────────────────
  canonical: 'Canonical URL',
  canonicalUrl: 'Canonical',
  addCanonical: 'Добавить правило',
  // ── A12.07 index policy ─────────────────────────────────────────
  indexPolicy: 'Политика индексации',
  policy: 'Политика',
  addPolicy: 'Добавить правило',
  // ── A12.08 noindex ──────────────────────────────────────────────
  noindexList: 'Список noindex',
  reason: 'Причина',
  addNoindex: 'Добавить путь',
  // ── A12.09 schema.org ───────────────────────────────────────────
  schemaOrg: 'Структурные данные (Schema.org)',
  schemaType: 'Тип',
  notes: 'Заметки',
  addSchema: 'Добавить',
  // ── A12.10 hreflang ─────────────────────────────────────────────
  hreflang: 'Управление hreflang',
  lang: 'Язык',
  href: 'URL',
  addHreflang: 'Добавить',
  // ── A12.22 landing / A12.23 blog ────────────────────────────────
  landing: 'Конструктор лендингов',
  blog: 'Блог / новости (CMS)',
  slug: 'Slug',
  titleCol: 'Заголовок',
  status: 'Статус',
  draft: 'Черновик',
  published: 'Опубликовано',
  addLanding: 'Добавить страницу',
  addBlog: 'Добавить запись',
  // ── A12.24 mini app / A12.25 deep-link ──────────────────────────
  miniAppSeo: 'SEO каталога Mini App',
  app: 'Приложение',
  addMiniApp: 'Добавить',
  deepLink: 'Превью deep-link',
  addDeepLink: 'Добавить',
  // ── Skeleton banner (SeoPlanned) ────────────────────────────────
  plannedBanner: 'Запланированный экран (скелет)',
  plannedNeeds: 'Требуется',
  plannedAdapterReady:
    'Каркас интерфейса + адаптер данных готовы — экран оживёт при подключении источника.',
  backToDashboard: 'К SEO панели',
  // ── Skeleton screen titles + needs ──────────────────────────────
  keywords: 'Реестр ключевых слов',
  keywordsNeeds: 'API отслеживания позиций (rank-tracker)',
  competitors: 'Анализ конкурентов',
  competitorsNeeds: 'SEO-аналитика (Ahrefs / Semrush API)',
  contentGaps: 'Пробелы контента',
  contentGapsNeeds: 'Пайплайн «конкуренты + ключевые слова»',
  internalLinks: 'Анализ внутренних ссылок',
  internalLinksNeeds: 'Краулер графа ссылок сайта',
  webVitals: 'Core Web Vitals',
  webVitalsNeeds: 'CrUX / RUM-телеметрия',
  pageSpeed: 'Скорость страниц',
  pageSpeedNeeds: 'PageSpeed Insights API',
  mobileFriendly: 'Мобильная совместимость',
  mobileFriendlyNeeds: 'Lighthouse / mobile-friendly test API',
  searchConsole: 'Интеграция Search Console',
  searchConsoleNeeds: 'OAuth Google Search Console',
  yandexWebmaster: 'Интеграция Yandex Webmaster',
  yandexWebmasterNeeds: 'OAuth Yandex Webmaster',
  seoAudit: 'Отчёт SEO-аудита',
  seoAuditNeeds: 'Движок краулинга + SEO-аудита',
  // ── A12.13 keyword registry ─────────────────────────────────────
  keyword: 'Ключевое слово',
  targetPage: 'Целевая страница',
  targetPosition: 'Целевая позиция',
  note: 'Заметка',
  addKeyword: 'Добавить слово',
  // ── A12.14 competitors ──────────────────────────────────────────
  domain: 'Домен',
  name: 'Название',
  addCompetitor: 'Добавить конкурента',
  // ── A12.15 content gaps ─────────────────────────────────────────
  topic: 'Тема',
  gapPlanned: 'Запланировано',
  gapInProgress: 'В работе',
  gapDone: 'Готово',
  addGap: 'Добавить пробел',
  // ── A12.16 internal links ───────────────────────────────────────
  anchor: 'Анкор',
  addLink: 'Добавить ссылку',
  // ── Config screens (A12.17–21) common ───────────────────────────
  settingsSaved: 'Настройки сохранены',
  secretSet: 'Настроено',
  secretHint: 'Оставьте пустым, чтобы не менять',
  secretPlaceholder: '•••••••• (скрыто)',
  metricsHint: 'Значения вводятся вручную или из внешнего инструмента.',
  // ── A12.17 Core Web Vitals ──────────────────────────────────────
  cwvUrl: 'URL страницы',
  cwvLcp: 'LCP, с',
  cwvInp: 'INP, мс',
  cwvCls: 'CLS',
  // ── A12.18 PageSpeed ────────────────────────────────────────────
  psKey: 'PageSpeed Insights API-ключ',
  psUrl: 'URL',
  psStrategy: 'Стратегия',
  psMobile: 'Мобильные',
  psDesktop: 'Десктоп',
  psScore: 'Оценка (0–100)',
  // ── A12.19 mobile-friendly checklist ────────────────────────────
  mobViewport: 'Meta viewport настроен',
  mobResponsive: 'Адаптивная вёрстка',
  mobTap: 'Тап-цели ≥ 48px',
  mobFont: 'Читаемый шрифт',
  yes: 'Да',
  no: 'Нет',
  // ── A12.20 Search Console ───────────────────────────────────────
  gscSite: 'URL сайта (property)',
  gscKey: 'API-ключ / OAuth-токен',
  gscClicks: 'Клики',
  gscImpressions: 'Показы',
  gscCtr: 'CTR, %',
  gscPosition: 'Ср. позиция',
  // ── A12.21 Yandex Webmaster ─────────────────────────────────────
  yaHost: 'Host ID',
  yaToken: 'OAuth-токен',
  yaSqi: 'ИКС (SQI)',
  yaIndexed: 'Страниц в индексе',
  // ── A12.26 audit ────────────────────────────────────────────────
  auditScore: 'Оценка SEO',
  auditIssues: 'Проблемы',
  auditRun: 'Обновить аудит',
  auditGeneratedAt: 'Сформировано',
  auditCheckCol: 'Проверка',
  auditDetailCol: 'Детали',
  auditCountCol: 'Кол-во',
  auditStatusCol: 'Статус',
  sevOk: 'OK',
  sevWarn: 'Внимание',
  sevError: 'Ошибка',
} as const
